import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Tooltip,
  Avatar,
  Badge,
  useTheme,
  CircularProgress,
  CardHeader,
  Divider,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Edit,
  Delete,
  Visibility,
  Timer,
  AttachMoney,
  CalendarToday,
  Person,
  MoreVert,
  Refresh,
  PlayArrow,
  Stop,
  Pause,
  ReceiptLong,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ReceiptExpenseUpload from '../../components/Billing/ReceiptExpenseUpload';
import CaseAutocomplete from '../../components/Common/CaseAutocomplete';
import ClientAutocomplete from '../../components/Common/ClientAutocomplete';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import ListPagination from '../../components/Common/ListPagination';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from '../../utils/datetimeLocal';
import { getAuthToken } from '../../lib/authToken';


interface TimeEntry {
  id: string;
  startTime: string;
  endTime?: string;
  duration: number; // in seconds
  description: string;
  caseId?: string;
  clientId?: string;
  taskType: string;
  billable: boolean;
  rate?: number;
  amount?: number;
  status: 'active' | 'paused' | 'completed';
  case?: {
    id: string;
    title: string;
    caseNumber: string;
  };
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface TimeEntriesResponse {
  success: boolean;
  data: {
    timeEntries: TimeEntry[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

const TimeEntriesPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    billable: '',
    taskType: '',
    search: '',
  });
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit' | 'create'>('view');
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [formData, setFormData] = useState<Partial<TimeEntry>>({
    description: '',
    startTime: new Date().toISOString(),
    endTime: undefined,
    duration: 0,
    taskType: 'legal_work',
    billable: true,
    rate: 0,
    caseId: undefined,
    clientId: undefined,
    status: 'active'
  });

  // Initialize form when opening editor
  React.useEffect(() => {
    if (!dialogOpen) return;
    if (dialogMode === 'edit' && selectedEntry) {
      setFormData({
        description: selectedEntry.description,
        startTime: selectedEntry.startTime,
        endTime: selectedEntry.endTime,
        duration: selectedEntry.duration,
        taskType: selectedEntry.taskType,
        billable: selectedEntry.billable,
        rate: selectedEntry.rate,
        caseId: selectedEntry.case?.id,
        clientId: selectedEntry.client?.id,
        status: selectedEntry.status,
      });
    }
    if (dialogMode === 'create') {
      setFormData({
        description: '',
        startTime: new Date().toISOString(),
        endTime: undefined,
        duration: 0,
        taskType: 'legal_work',
        billable: true,
        rate: 0,
        caseId: undefined,
        clientId: undefined,
        status: 'active',
      });
    }
  }, [dialogOpen, dialogMode, selectedEntry]);

  // Fetch time entries
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['time-entries', user?.tenantId, filters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.set('page', String(page));
      params.set('limit', String(limit));
      
      const token = getAuthToken();
      const response = await fetch(`/api/time-entries?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch time entries');
      }
      return response.json();
    },
    keepPreviousData: true,
  });

  // Update time entry mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, entryData }: { id: string; entryData: Partial<TimeEntry> }) => {
      const token = getAuthToken();
      const response = await fetch(`/api/time-entries/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entryData),
      });
      if (!response.ok) {
        throw new Error('Failed to update time entry');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Time entry updated successfully');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      setDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Create time entry mutation
  const createMutation = useMutation({
    mutationFn: async (entryData: Partial<TimeEntry>) => {
      const token = getAuthToken();
      const response = await fetch(`/api/time-entries`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entryData),
      });
      if (!response.ok) {
        throw new Error('Failed to create time entry');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Time entry created successfully');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      setDialogOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Delete time entry mutation
  const deleteMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const token = getAuthToken();
      const response = await fetch(`/api/time-entries/${entryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete time entry');
      }
      return response.json();
    },
    onSuccess: () => {
      toast.success('Time entry deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = (entryId: string) => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      deleteMutation.mutate(entryId);
    }
  };

  const handleViewEntry = (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const handleEditEntry = (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleSaveEntry = async () => {
    if (dialogMode === 'edit' && selectedEntry) {
      await updateMutation.mutateAsync({ id: selectedEntry.id, entryData: formData });
    } else if (dialogMode === 'create') {
      await createMutation.mutateAsync(formData);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'completed':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getTaskTypeColor = (taskType: string) => {
    switch (taskType) {
      case 'legal_work':
        return 'primary';
      case 'research':
        return 'info';
      case 'client_meeting':
        return 'success';
      case 'court_appearance':
        return 'warning';
      case 'document_preparation':
        return 'secondary';
      case 'consultation':
        return 'info';
      case 'administrative':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numAmount || 0);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  const getTaskTypeLabel = (taskType: string) => {
    const labels: { [key: string]: string } = {
      legal_work: 'Legal Work',
      research: 'Research',
      client_meeting: 'Client Meeting',
      court_appearance: 'Court Appearance',
      document_preparation: 'Document Preparation',
      consultation: 'Consultation',
      administrative: 'Administrative',
    };
    return labels[taskType] || taskType;
  };

  if (error) {
    console.error('Time entries error:', error);
  }

  const timeEntries = data?.data?.timeEntries || [];
  const timeEntriesTotal = data?.data?.pagination?.total || timeEntries.length;

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="time entries"
          onRetry={() => refetch()}
        />
      )}
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Time Entries
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ReceiptLong />}
              onClick={() => setExpenseOpen(true)}
            >
              Upload Receipt
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => { setSelectedEntry(null); setDialogMode('create'); setDialogOpen(true); }}
            >
              New Entry
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Track and manage your billable time entries.
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search time entries..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Billable</InputLabel>
                <Select
                  value={filters.billable}
                  onChange={(e) => setFilters({ ...filters, billable: e.target.value })}
                  label="Billable"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Billable</MenuItem>
                  <MenuItem value="false">Non-Billable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Task Type</InputLabel>
                <Select
                  value={filters.taskType}
                  onChange={(e) => setFilters({ ...filters, taskType: e.target.value })}
                  label="Task Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="legal_work">Legal Work</MenuItem>
                  <MenuItem value="research">Research</MenuItem>
                  <MenuItem value="client_meeting">Client Meeting</MenuItem>
                  <MenuItem value="court_appearance">Court Appearance</MenuItem>
                  <MenuItem value="document_preparation">Document Preparation</MenuItem>
                  <MenuItem value="consultation">Consultation</MenuItem>
                  <MenuItem value="administrative">Administrative</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setFilters({ status: '', billable: '', taskType: '', search: '' });
                  refetch();
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : timeEntries.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No time entries found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start tracking your time to see entries here.
              </Typography>
            </Box>
          ) : (
            <>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Case/Client</TableCell>
                    <TableCell>Task Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Billable</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeEntries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {entry.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(entry.startTime)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {formatTime(entry.duration)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {entry.case ? (
                          <Box>
                            <Typography variant="body2">
                              {entry.case.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.case.caseNumber}
                            </Typography>
                          </Box>
                        ) : entry.client ? (
                          <Box>
                            <Typography variant="body2">
                              {entry.client.firstName} {entry.client.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.client.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No case/client
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTaskTypeLabel(entry.taskType)}
                          color={getTaskTypeColor(entry.taskType) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.status}
                          color={getStatusColor(entry.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.billable ? 'Billable' : 'Non-Billable'}
                          color={entry.billable ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {entry.amount ? (
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(entry.amount)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(entry.startTime)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewEntry(entry)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleEditEntry(entry)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(entry.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <ListPagination
              page={page}
              limit={limit}
              total={timeEntriesTotal}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* Time Entry Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'view' ? 'View Time Entry' : dialogMode === 'edit' ? 'Edit Time Entry' : 'New Time Entry'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'view' && selectedEntry && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {selectedEntry.description}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Duration</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{formatTime(selectedEntry.duration)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="body1">{selectedEntry.amount ? formatCurrency(selectedEntry.amount) : '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Start Time</Typography>
                  <Typography variant="body1">{formatDate(selectedEntry.startTime)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">End Time</Typography>
                  <Typography variant="body1">{selectedEntry.endTime ? formatDate(selectedEntry.endTime) : '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Task Type</Typography>
                  <Chip label={getTaskTypeLabel(selectedEntry.taskType)} color={getTaskTypeColor(selectedEntry.taskType) as any} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={selectedEntry.status} color={getStatusColor(selectedEntry.status) as any} size="small" />
                </Grid>
              </Grid>
            </Box>
          )}

          {(dialogMode === 'edit' || dialogMode === 'create') && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    value={formData.description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Start Time"
                    type="datetime-local"
                    fullWidth
                    value={toDatetimeLocalValue(formData.startTime)}
                    onChange={(e) => {
                      const iso = fromDatetimeLocalValue(e.target.value);
                      setFormData((prev) => ({ ...prev, startTime: iso || prev.startTime || new Date().toISOString() }));
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End Time"
                    type="datetime-local"
                    fullWidth
                    value={toDatetimeLocalValue(formData.endTime)}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        endTime: fromDatetimeLocalValue(e.target.value) || undefined,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Duration (seconds)"
                    type="number"
                    fullWidth
                    value={formData.duration ?? 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value || '0', 10) }))}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Task Type</InputLabel>
                    <Select
                      label="Task Type"
                      value={formData.taskType || 'legal_work'}
                      onChange={(e) => setFormData(prev => ({ ...prev, taskType: e.target.value as any }))}
                    >
                      <MenuItem value="legal_work">Legal Work</MenuItem>
                      <MenuItem value="research">Research</MenuItem>
                      <MenuItem value="client_meeting">Client Meeting</MenuItem>
                      <MenuItem value="court_appearance">Court Appearance</MenuItem>
                      <MenuItem value="document_preparation">Document Preparation</MenuItem>
                      <MenuItem value="consultation">Consultation</MenuItem>
                      <MenuItem value="administrative">Administrative</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Billable</InputLabel>
                    <Select
                      label="Billable"
                      value={formData.billable ? 'true' : 'false'}
                      onChange={(e) => setFormData(prev => ({ ...prev, billable: e.target.value === 'true' }))}
                    >
                      <MenuItem value="true">Billable</MenuItem>
                      <MenuItem value="false">Non-Billable</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Rate ($/hr)"
                    type="number"
                    fullWidth
                    value={formData.rate ?? 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, rate: parseFloat(e.target.value || '0') }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CaseAutocomplete
                    label="Related case"
                    value={formData.caseId || null}
                    onChange={(c) =>
                      setFormData((prev) => ({
                        ...prev,
                        caseId: c?.id || undefined,
                        clientId: c?.clientId || c?.client?.id || prev.clientId,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <ClientAutocomplete
                    label="Related client"
                    value={formData.clientId || null}
                    onChange={(c) =>
                      setFormData((prev) => ({
                        ...prev,
                        clientId: c?.id || undefined,
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {(dialogMode === 'edit' || dialogMode === 'create') && (
            <Button
              onClick={handleSaveEntry}
              variant="contained"
              disabled={updateMutation.isLoading || createMutation.isLoading}
            >
              {dialogMode === 'edit' ? (updateMutation.isLoading ? 'Saving...' : 'Save Changes') : (createMutation.isLoading ? 'Creating...' : 'Create Entry')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ReceiptExpenseUpload
        open={expenseOpen}
        onClose={() => setExpenseOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['time-entries'] });
          queryClient.invalidateQueries({ queryKey: ['case-billing-items'] });
        }}
      />
    </Box>
  );
};

export default TimeEntriesPage; 